<template>
  <!-- With dropdown -->
  <DropdownMenuRoot v-if="hasDropdown" v-model:open="isOpen" :modal="false">
    <DropdownMenuTrigger as-child>
      <span v-tooltip="tooltip" class="icon-btn-wrap">
        <component
          :is="tag"
          ref="triggerRef"
          class="icon-btn"
          :class="rootClasses"
          type="button"
          :disabled="isEffectivelyDisabled"
          :tabindex="tag === 'a' ? 0 : undefined"
          @click.prevent
        >
          <span v-if="badge != null" class="icon-btn__badge">{{ badge }}</span>
          <span class="icon" :class="sizeClass">
            <i :class="normalizedIcon" />
          </span>
          <span v-if="showArrow" class="icon-btn__caret icon">
            <i class="fa-solid fa-angle-down" />
          </span>
        </component>
      </span>
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent
        :align="align"
        side="bottom"
        :side-offset="4"
        class="icon-btn-dd"
        @click="handleDropdownClick"
      >
        <slot name="dropdown" />
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>

  <!-- Without dropdown -->
  <span v-else v-tooltip="tooltip" class="icon-btn-wrap">
    <component
      :is="tag"
      class="icon-btn"
      :class="rootClasses"
      type="button"
      :disabled="disabled"
      :tabindex="tag === 'a' ? 0 : undefined"
      @click="handleClick"
    >
      <span v-if="badge != null" class="icon-btn__badge">{{ badge }}</span>
      <span class="icon" :class="sizeClass">
        <i :class="normalizedIcon" />
      </span>
    </component>
  </span>
</template>

<script>
import {
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'reka-ui'
import { computed, ref, watch } from 'vue'

function normalizeIcon(icon) {
  if (!icon) return []
  if (Array.isArray(icon)) return icon
  return icon.split(' ')
}

export default {
  name: 'IconButton',
  components: {
    DropdownMenuRoot,
    DropdownMenuTrigger,
    DropdownMenuPortal,
    DropdownMenuContent,
  },
  props: {
    icon: { type: [String, Array], required: true },
    color: { type: String, default: 'primary' },
    size: { type: String, default: 'normal' },
    colorful: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    tooltip: { type: [String, Object], default: null },
    badge: { type: [String, Number], default: null },
    showArrow: { type: Boolean, default: true },
    align: {
      type: String,
      default: 'start',
      validator: (v) => ['start', 'center', 'end'].includes(v),
    },
    tag: {
      type: String,
      default: 'button',
      validator: (v) => ['button', 'a'].includes(v),
    },
  },
  emits: ['click'],
  setup(props, { emit, slots }) {
    const isOpen = ref(false)
    const triggerRef = ref(null)

    const normalizedIcon = computed(() => {
      const base = normalizeIcon(props.icon)
      return props.loading ? [...base, 'fa-spin'] : base
    })
    const isEffectivelyDisabled = computed(
      () => props.disabled || props.loading,
    )
    const hasDropdown = computed(() => !!slots.dropdown)

    const sizeClass = computed(() => {
      if (props.size === 'small') return 'is-small'
      if (props.size === 'medium') return 'is-medium'
      if (props.size === 'large') return 'is-large'
      return null
    })

    const rootClasses = computed(() => [
      props.color && `is-${props.color}`,
      props.size !== 'normal' && `is-${props.size}`,
      {
        'is-colorful': props.colorful || props.badge != null,
        'is-disabled': isEffectivelyDisabled.value,
        'has-caret': hasDropdown.value && props.showArrow,
        'has-badge': props.badge != null,
      },
    ])

    const handleClick = (e) => {
      if (!isEffectivelyDisabled.value) emit('click', e)
    }

    // Auto-close when a link inside the dropdown is clicked.
    // Needed because slot content uses plain <a> tags, not reka-ui
    // DropdownMenuItem (which would auto-close via @select).
    const handleDropdownClick = (e) => {
      if (e.target.closest('a, .dd-item')) {
        isOpen.value = false
      }
    }

    // Blur trigger after close so tooltip doesn't re-appear
    watch(isOpen, (open) => {
      if (!open) {
        const el = triggerRef.value?.$el || triggerRef.value
        if (el && el.contains(document.activeElement)) el.blur()
      }
    })

    return {
      isOpen,
      triggerRef,
      normalizedIcon,
      hasDropdown,
      sizeClass,
      rootClasses,
      isEffectivelyDisabled,
      handleClick,
      handleDropdownClick,
    }
  },
}
</script>

<style lang="scss" scoped>
.icon-btn-wrap {
  display: inline-flex;
}

.icon-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0.15em;
  cursor: pointer;
  border-radius: var(--radius-sm, 4px);
  color: var(--text-default);
  font-size: 1em;

  &.is-small {
    font-size: 0.75em;
  }
  &.is-medium {
    font-size: 1.25em;
  }
  &.is-large {
    font-size: 1.5em;
  }
  transition:
    color 0.15s,
    background-color 0.15s;

  &:not([disabled]):hover,
  &:not([disabled]):focus-visible {
    background-color: var(--bg-default);
  }

  // -- Color variants --
  &.is-danger {
    --icon-btn-color: var(--danger);
  }
  &.is-primary {
    --icon-btn-color: var(--primary);
  }
  &.is-success {
    --icon-btn-color: var(--success);
  }
  &.is-warning {
    --icon-btn-color: var(--warning);
  }
  &.is-info {
    --icon-btn-color: var(--info);
  }
  &.is-link {
    --icon-btn-color: var(--link);
  }

  // Color applies on hover/focus or when always-colorful
  &:not([disabled]):hover,
  &:not([disabled]):focus-visible,
  &.is-colorful {
    color: var(--icon-btn-color, currentColor);
  }

  // -- Disabled --
  &.is-disabled,
  &[disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// Caret for dropdown indicator
.icon-btn__caret {
  margin-left: -0.55em;
  font-size: 0.7em;
  opacity: 0.5;
}

// Badge (top-right count indicator)
.icon-btn__badge {
  position: absolute;
  top: -0.6em;
  right: -0.6em;
  min-width: 1.4em;
  height: 1.4em;
  padding: 0 0.3em;
  border-radius: var(--radius-full);
  background: var(--icon-btn-color, var(--primary));
  color: var(--white);
  outline: 2px solid var(--white);
  font-size: 0.7em;
  font-weight: 700;
  line-height: 1.4em;
  text-align: center;
  pointer-events: none;
  z-index: 1;

  // Empty badge (dot indicator, no text)
  &:empty {
    min-width: 0.7em;
    height: 0.7em;
    padding: 0;
    top: -0.1em;
    right: -0.1em;
  }
}

// Shift badge right when caret is present
.has-caret > .icon-btn__badge {
  right: -0.1em;
}

// Portal dropdown styles in ui-elements.scss
</style>
